/**
 * ftModal PHASE 2 — native deep-diff snapshot helpers for the field-editor modal (diff-save fields).
 *
 * Reproduces the v1 `changesTracker` snapshot/diff contract (account-v1/.../changes-tracker/
 * changesTracker.js) WITHOUT the AngularJS `$scope.$watch` machinery — and WITHOUT the
 * `$injector`/`ftModal`/`ft-snapshot` bridge. A diff-save host (e.g. contest-details
 * editContestName) deep-CLONES the datasource on modal-open, the native field mutates the live
 * object, and on DONE the host diffs live-vs-snapshot to produce the EXACT v1 deep-diff records
 * (`{kind:'E'|'N'|'D'|'A', path:[...], lhs, rhs}`) that `changesTransformer` consumes; on CANCEL
 * it reverts each change (matching v1 `tracker.rollback()` → `DeepDiff.revertChange`).
 *
 * DeepDiff source: `./vendor/deep-diff` — a verbatim copy of `deep-diff` v1.0.2's `index.js`, the
 * SAME library v1 loaded as `$window.DeepDiff` (the `flipto.core.DeepDiff` factory was literally
 * that global). The npm package is deprecated ("Package no longer supported"), so it was vendored
 * rather than swapped: the change records below are a wire contract, not an implementation detail.
 * See `./vendor/NOTICE.md` for the provenance, licence and re-sync notes, and
 * `./native-diff.util.spec.ts` for the characterization spec that locks the record shapes.
 *
 * The `(_path, key)` prefilter is reproduced VERBATIM from changesTracker.js:185-187: prune any key
 * starting with `$` (Angular `$$hashKey` etc.) or named `constructor`.
 */
import { diff as diffImpl, revertChange as revertChangeImpl } from './vendor/deep-diff';

/**
 * deep-diff v1.x signatures. The vendored module is untyped JS under `@ts-nocheck`, and its inferred
 * arity carries upstream's fourth `accum` parameter that this app never passes, so re-state the two
 * signatures here rather than call through an `any`.
 */
type DiffFn = (lhs: any, rhs: any, prefilter?: (path: any[], key: string) => boolean) => any[] | undefined;
type RevertChangeFn = (target: any, source: any, change: any) => void;

const deepDiff = diffImpl as DiffFn;
const revertChange = revertChangeImpl as RevertChangeFn;

/** v1 changesTracker.js:185-187 prefilter — return true to PRUNE a key. */
function prefilter(_path: any[], key: any): boolean {
  const k = key == null ? '' : key.toString();
  return k.indexOf('$') === 0 || k === 'constructor';
}

/**
 * Open-time deep clone (the rollback baseline + diff origin). Mirrors v1
 * `changesTracker.createSnapshot`'s `angular.copy`. Prefers `structuredClone`; falls back to a
 * JSON round-trip. Field-editor datasources are deserialised API data (strings/numbers/bools/
 * plain objects/arrays + Angular `$$hashKey` strings) — JSON-safe, no moments/functions — so both
 * clone paths produce type-faithful snapshots (the prefilter drops `$$hashKey` from any diff).
 */
export function snapshotClone<T>(obj: T): T {
  if (typeof (globalThis as any).structuredClone === 'function') {
    try {
      return (globalThis as any).structuredClone(obj);
    } catch {
      /* structuredClone throws on functions/proxies — fall through to JSON. */
    }
  }
  return obj == null ? obj : (JSON.parse(JSON.stringify(obj)) as T);
}

/**
 * Compute the v1 deep-diff change array between a `snapshot` (lhs) and the `live` object (rhs),
 * with the v1 prefilter. Returns `[]` when identical. Argument order matches v1
 * `DeepDiff.diff(origin /*snapshot*\/, source /*live*\/, prefilter)`.
 */
export function computeDiffs(snapshot: any, live: any): any[] {
  return deepDiff(snapshot, live, prefilter) || [];
}

/**
 * Revert each change on `live` back to `snapshot` — matching v1 `tracker.rollback()`
 * (`DeepDiff.revertChange(target /*live*\/, source /*snapshot*\/, change)`). Used by a diff-save
 * host's CANCEL to undo the native field's in-place edits.
 */
export function revertDiffs(live: any, snapshot: any, diffs: any[]): void {
  for (const change of diffs) {
    if (change && !change.path) {
      // deep-diff bug: revertChange (unlike applyChange, vendor/deep-diff.ts) assumes change.path
      // exists, but root-level changes — e.g. diffing two arrays directly yields pathless kind:'A'
      // records — have none, so it crashes reading `change.path.length`. Revert through a synthetic
      // parent so navigation lands on `live` itself; revertArrayChange then mutates it in place.
      revertChange({ root: live }, { root: snapshot }, { ...change, path: ['root'] });
    } else {
      revertChange(live, snapshot, change);
    }
  }
}
